"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowRight, UserCheck } from "lucide-react";

import { useAuthStore, landingPathForRole } from "@/lib/auth-store";
import { MosaicBackground } from "@/components/landing/mosaic-background";
import { LandingNav } from "@/components/landing/landing-nav";
import { HeroSection } from "@/components/landing/hero-section";
import { MissionProblemSection } from "@/components/landing/mission-problem-section";
import { DualEngineSection } from "@/components/landing/dual-engine-section";
import { AthleticScienceExplorer } from "@/components/landing/athletic-science-explorer";
import { ModulesGrid } from "@/components/landing/modules-grid";
import { VideoShowcaseSection } from "@/components/landing/video-showcase-section";
import { PlatformGallerySection } from "@/components/landing/platform-gallery-section";
import { FoundersStorySection } from "@/components/landing/founders-story-section";
import { TrustSecuritySection } from "@/components/landing/trust-security-section";
import { ContactSupportSection } from "@/components/landing/contact-support-section";
import { LandingFooter } from "@/components/landing/landing-footer";
import { BookDemoModal } from "@/components/landing/book-demo-modal";
import { LegalModals, LegalDocType } from "@/components/landing/legal-modals";

export default function OlympioHomePage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType>(null);
  const { status, user, hydrate } = useAuthStore();

  useEffect(() => {
    if (status === "idle") hydrate();
  }, [status, hydrate]);

  const handleOpenVideo = () => {
    const videoSection = document.getElementById("video");
    if (videoSection) {
      videoSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#1A3C2B] font-body selection:bg-[#1A3C2B] selection:text-white overflow-x-hidden relative">
      {/* Mosaic SVG Background Layer */}
      <MosaicBackground />

      {/* Logged in notification pill if user has an active session */}
      {status === "authenticated" && user && (
        <div className="fixed bottom-6 right-6 z-40">
          <Link
            href={landingPathForRole(user.role)}
            className="flex items-center gap-2.5 px-4 py-2.5 bg-[#1A3C2B] text-white font-mono-tech text-xs uppercase tracking-wider border border-[#1A3C2B] hover:bg-[#132d20] transition-all rounded-none shadow-lg"
          >
            <UserCheck className="w-4 h-4 text-[#9EFFBF]" />
            <span>Active Session ({user.role}): Return to Dashboard</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* Navigation */}
      <LandingNav
        onOpenDemo={() => setDemoModalOpen(true)}
        onOpenVideo={handleOpenVideo}
      />

      {/* Main Content Sections */}
      <main className="relative z-10">
        {/* Hero Section */}
        <HeroSection
          onOpenDemo={() => setDemoModalOpen(true)}
          onOpenVideo={handleOpenVideo}
        />

        {/* 5 Structural Failures Problem Statement */}
        <MissionProblemSection />

        {/* Dual-Engine Architecture */}
        <DualEngineSection onOpenDemo={() => setDemoModalOpen(true)} />

        {/* Athletic Science & Interactive Somatic Calculator */}
        <AthleticScienceExplorer />

        {/* Bento Feature Grid */}
        <ModulesGrid onOpenDemo={() => setDemoModalOpen(true)} />

        {/* 3-Minute Cinematic Video Showcase */}
        <VideoShowcaseSection />

        {/* Platform Live Screens Gallery & Lightbox */}
        <PlatformGallerySection />

        {/* Founders Story & Manifesto */}
        <FoundersStorySection onOpenDemo={() => setDemoModalOpen(true)} />

        {/* Security, Trust & DPDP Act 2023 */}
        <TrustSecuritySection onOpenLegal={doc => setLegalDoc(doc)} />

        {/* Technical Form CTA (#contact) */}
        <ContactSupportSection onOpenDemo={() => setDemoModalOpen(true)} />
      </main>

      {/* Footer */}
      <LandingFooter
        onOpenDemo={() => setDemoModalOpen(true)}
        onOpenLegal={doc => setLegalDoc(doc)}
      />

      {/* Interactive Modals */}
      <BookDemoModal
        isOpen={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
      />

      <LegalModals
        activeDoc={legalDoc}
        onClose={() => setLegalDoc(null)}
      />
    </div>
  );
}
